// The auto re-queue notice (2026-10-07, Lab-only behind PARKING_ENABLED): a
// park open 3 hours was ended by the server and the contact went back to the
// queue. Its agent sees this, one notice at a time, oldest first, until they
// answer it. The details are the Parked tab's own fields (ParkedView.jsx's
// parkFields()). Ok acknowledges; Re-Park parks the same contact again with
// the same note (claiming it when nobody owns it, or on the agent's own claim).
// Neither moves the agent: no navigation, no selection change. View in new tab
// (2026-10-08) opens the contact at its deep link in a new browser tab and
// leaves the notice open and unanswered, so the agent can look, then choose.
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { parkFields } from './ParkedView.jsx'
import { formatParkedDuration, parkDurationMs, reparkBlockedReason, parkedContactsText } from './parking.js'
import { convNumberPath } from './contactPath.js'
import { formatTime12h } from './scheduleStatus.js'

// The parking notices' one modal (2026-10-08): the re-queue notice and the
// end-of-shift reminder share it, so both look and sit the same way.
export function NoticeDialog({ title, testId, children, actions }) {
  return (
    <Dialog open maxWidth="sm" fullWidth data-testid={testId}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  )
}

// The end-of-shift reminder (2026-10-08), in the same modal as the re-queue
// notice: "View Parked Contacts" opens the Parked page in a new tab, Dismiss
// closes it. Both mark it seen for this shift (App.jsx).
export function ShiftEndReminderNotice({ reminder, onViewParked, onDismiss }) {
  if (!reminder) return null
  return (
    <NoticeDialog
      title="Shift ending soon"
      testId="shift-end-reminder"
      actions={(
        <>
          <Button onClick={onViewParked}>View Parked Contacts</Button>
          <Button variant="contained" onClick={onDismiss}>Dismiss</Button>
        </>
      )}
    >
      <Typography variant="body2">
        Your shift ends at {formatTime12h(reminder.shiftEnd)}. You have {parkedContactsText(reminder.count)}.
      </Typography>
    </NoticeDialog>
  )
}

export default function AutoRequeueNotice({ notice, busy = false, error = null, onOk, onRepark }) {
  if (!notice) return null
  const duration = parkDurationMs(notice)
  const blocked = reparkBlockedReason(notice)
  const convNumber = notice.conversation?.convNumber ?? null
  return (
    <NoticeDialog
      title="Contact re-queued"
      testId="auto-requeue-notice"
      actions={(
        <>
          <Button onClick={() => window.open(convNumberPath(convNumber), '_blank', 'noopener')} disabled={convNumber == null}>View in new tab</Button>
          <Button onClick={onRepark} disabled={busy || Boolean(blocked)}>Re-Park This Contact</Button>
          <Button variant="contained" onClick={onOk} disabled={busy}>Ok</Button>
        </>
      )}
    >
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        This contact was parked for {formatParkedDuration(duration)} and has been automatically re-queued.
      </Typography>
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}>
        {parkFields(notice, { durationMs: duration }).map((f) => (
          <Box key={f.label} sx={{ display: 'contents' }}>
            <Typography component="dt" variant="body2" color="text.secondary">{f.label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, ...(f.sx ?? {}) }} data-testid={f.testId}>{f.value}</Typography>
          </Box>
        ))}
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      {blocked && <Alert severity="info" sx={{ mt: 1.5 }} data-testid="repark-blocked">{blocked}</Alert>}
    </NoticeDialog>
  )
}
